import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { downloadFileFromAssets } from '@dealstack/frontend/shared/files/downloadFile';
import { Flex, Text } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import { memo, useMemo, useState } from 'react';
import styled from 'styled-components';

import { getInstructionFileUrl, KycInstruction } from './KycInstruction';

interface Props {
	className?: string;
	isCertifiedCopiesOfDocumentsRequested: boolean;
}

const KycInstructionBar = ({ isCertifiedCopiesOfDocumentsRequested, className }: Props) => {
	const tInvestor = useTranslationInvestor();
	const [opened, setOpened] = useState(false);

	const ref = useClickOutside(() => {
		setOpened(false);
	});

	const instructionFileUrl = useMemo(() => {
		return getInstructionFileUrl(isCertifiedCopiesOfDocumentsRequested);
	}, [isCertifiedCopiesOfDocumentsRequested]);

	return (
		<div className={className}>
			{opened ? (
				<KycInstruction
					onClose={() => {
						setOpened(false);
					}}
					ref={ref}
					limitHeight
					isCertifiedCopiesOfDocumentsRequested={isCertifiedCopiesOfDocumentsRequested}
				/>
			) : (
				<Flex
					justify="space-between"
					align="center"
					w="31rem"
					sx={(theme) => ({
						borderTop: `4px solid ${theme.colors.blue[6]}`,
						borderRadius: '4px',
						padding: '1rem 0.5rem 0.5rem 0.5rem',
						boxShadow: '0 4px 8px 0 #B8BFC2',
					})}
				>
					<Text size="lg" fw={600}>
						{tInvestor('investment.join.kyc.instr.title')}
					</Text>

					<Flex gap="1rem">
						<Icon
							variant="download-v3"
							size="xl"
							onClick={() => {
								downloadFileFromAssets(instructionFileUrl);
							}}
						/>
						<Icon
							variant="caret-up"
							size="xl"
							onClick={() => {
								setOpened(true);
							}}
						/>
					</Flex>
				</Flex>
			)}
		</div>
	);
};

const Memoized = memo(KycInstructionBar);
const Styled = styled(Memoized)`
	position: fixed;
	z-index: ${Z_INDEX.MODAL};
	background-color: ${(props) => props.theme.v3.colors.white[0]};
	bottom: 0;
	right: 5rem;

	${KycInstruction} {
		width: 50rem;
		min-width: 50rem;
	}
`;

export { Styled as KycInstructionBar };
